import { apiClient, handleApiResponse } from './client'
import type {
  Experiment,
  ApiResponse,
  FunnelAnalytics,
  RiskLevel,
} from '@/types'
import { authService } from '@/api/auth'

export interface CreateExperimentRequest {
  name: string
  hypothesis: string
  objective: string
  type: string
  tier: number
  is_negative?: boolean
  /** Relative-% MDE → BB+ROPE band (§8.1). */
  mde?: number | null
  /** Confirmatory segment (§8.3). */
  primary_segment?: 'overall' | 'mobile' | 'desktop'
  /** Decision posture (RISK-LEVELS-COPY.md). Omitted → store default → 'balanced'. */
  risk_level?: RiskLevel
  /** Hard-to-revert change (gates 'agile' in the UI). */
  hard_to_revert?: boolean
  status?: 'draft' | 'active' | 'paused' | 'completed'
  traffic_split?: {
    control: number
    treatment: number
  }
  targeting?: {
    url_patterns: string
    device_conditions: {
      desktop: boolean
      mobile: boolean
      tablet: boolean
    }
    exclusions: string
  }
  variants?: {
    control: any
    treatment: any
  }
  funnel_config?: {
    use_default: boolean
    selected_steps: string[]
    custom_steps: any[]
  }
  // EXP_TIMING_001: Optional timing and session limits
  timing?: {
    start_date_time?: string // ISO 8601 UTC datetime
    end_date_time?: string   // ISO 8601 UTC datetime
  }
  session_limit?: number
}

export interface CloneExperimentRequest {
  mode: 'repeat'
  name?: string
  note?: string
  timing?: 'same_duration' | 'none'
}

export interface ConfigureExperimentRequest {
  traffic_split: {
    control: number
    treatment: number
  }
  targeting: {
    url_patterns: string
    device_conditions: {
      desktop: boolean
      mobile: boolean
      tablet: boolean
    }
    exclusions: string
  }
  variants: {
    control: any
    treatment: any
  }
  target_selector?: {
    element: string
    position: string
    validated: boolean
    elements_count: number
  }
  funnel_config?: {
    use_default: boolean
    selected_steps: string[]
    custom_steps: any[]
  }
  // EXP_TIMING_001: Optional timing and session limits
  timing?: {
    start_date_time?: string // ISO 8601 UTC datetime
    end_date_time?: string   // ISO 8601 UTC datetime
  }
  session_limit?: number
}

class ExperimentService {
  // List all experiments
  async getExperiments(): Promise<Experiment[]> {
    const storeId = authService.getCurrentStoreId()
    const params = storeId ? { store_id: storeId } : undefined
    const response: ApiResponse<{experiments: Experiment[], count: number}> = await apiClient.get(
      '/api/experiments',
      params
    )
    const data = handleApiResponse(response)
    return data.experiments || []
  }

  // Get single experiment
  async getExperiment(id: string): Promise<Experiment> {
    const response: ApiResponse<Experiment> = await apiClient.get(
      `/api/experiments/${id}`
    )
    return handleApiResponse(response)
  }

  // Create new experiment
  async createExperiment(data: CreateExperimentRequest): Promise<Experiment> {
    const storeId = authService.getCurrentStoreId()
    const payload = {
      ...data,
      storeId: storeId
    }
    
    const response: ApiResponse<Experiment> = await apiClient.post(
      '/api/experiments',
      payload
    )
    return handleApiResponse(response)
  }

  // Configure experiment (not used in current flow, kept for compatibility)
  async configureExperiment(
    id: string,
    data: ConfigureExperimentRequest
  ): Promise<Experiment> {
    const response: ApiResponse<Experiment> = await apiClient.put(
      `/api/experiments/${id}`,
      data as any
    )
    return handleApiResponse(response)
  }

  // Preview and launch (not used; status can be updated via updateExperiment)
  async previewExperiment(
    id: string,
    launch: boolean = false
  ): Promise<Experiment> {
    const response: ApiResponse<Experiment> = await apiClient.put(
      `/api/experiments/${id}`,
      launch ? { status: 'active' } : {}
    )
    return handleApiResponse(response)
  }

  // Get experiment results
  async getExperimentResults(
    id: string,
    options: { window?: number; include_daily?: boolean } = {}
  ): Promise<any> {
    const params = new URLSearchParams()
    if (options.window) params.append('window', options.window.toString())
    if (options.include_daily !== undefined) {
      params.append('include_daily', options.include_daily.toString())
    }

    const queryString = params.toString()
    const url = `/api/experiments/${id}/results${queryString ? `?${queryString}` : ''}`

    const response = await apiClient.get(url)
    return handleApiResponse(response)
  }

  // Get experiment funnel analytics
  async getExperimentFunnel(id: string, windowDays?: number): Promise<FunnelAnalytics> {
    const storeId = authService.getCurrentStoreId()

    const params: Record<string, string | number> = {}
    if (windowDays) params.window = windowDays
    if (storeId) params.store_id = storeId

    const response: ApiResponse<FunnelAnalytics> = await apiClient.get(
      `/api/experiments/${id}/funnel`,
      Object.keys(params).length > 0 ? params : undefined
    )
    return handleApiResponse(response)
  }

  // Delete experiment
  async deleteExperiment(id: string): Promise<void> {
    await apiClient.delete(`/api/experiments/${id}`)
  }

  // Update experiment
  async updateExperiment(id: string, data: Partial<Experiment>): Promise<Experiment> {
    const response: ApiResponse<Experiment> = await apiClient.put(
      `/api/experiments/${id}`,
      data
    )
    return handleApiResponse(response)
  }

  /**
   * Clone (CLONE-EXPERIMENT-SPEC.md §3). Routed through the create POST with `clone_from` —
   * no dedicated API Gateway resource. `repeat` returns a DRAFT and archives the source; the
   * caller decides whether to launch it.
   */
  async cloneExperiment(sourceId: string, data: CloneExperimentRequest): Promise<Experiment> {
    const response: ApiResponse<Experiment> = await apiClient.post('/api/experiments', {
      ...data,
      clone_from: sourceId,
      storeId: authService.getCurrentStoreId(),
    })
    return handleApiResponse(response)
  }

  // Pause/Resume experiment
  async pauseExperiment(id: string): Promise<Experiment> {
    return this.updateExperiment(id, { status: 'paused' })
  }

  async resumeExperiment(id: string): Promise<Experiment> {
    return this.updateExperiment(id, { status: 'active' })
  }

  // --- Optimization slot controls (OPTIMIZATIONS-SPEC.md §5) ---------------
  // Every one of these bumps `optimization.version` server-side, which is what makes a merchant
  // instruction beat an allocator write already in flight. The split changes immediately (a pin
  // that took until tomorrow to show up would read as broken), and history is never destroyed.

  /** Serve one banner 100% of the time. The freshness watch keeps running while pinned. */
  async pinOptimizationArm(id: string, arm: string): Promise<Experiment> {
    return this.updateExperiment(id, { optimization: { pinned_arm: arm } } as any)
  }

  /** Back to letting the allocator choose, on the history it never lost. */
  async unpinOptimization(id: string): Promise<Experiment> {
    return this.updateExperiment(id, { optimization: { pinned_arm: null } } as any)
  }

  /** Take a banner out of the rotation (its data stays readable). */
  async retireOptimizationArms(id: string, retiredArms: string[]): Promise<Experiment> {
    return this.updateExperiment(id, { optimization: { retired_arms: retiredArms } } as any)
  }

  /** After a drift reset: the merchant re-confirmed their banner, so the guard wakes up again. */
  async reconfirmOptimizationControl(id: string): Promise<Experiment> {
    return this.updateExperiment(id, { optimization: { control_reconfirmed: true } } as any)
  }
}

export const experimentService = new ExperimentService()
